import React, { useState, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import { Typography, Table, Image, message } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'

import useRefundList from '@src/hooks/refund/useRefundList'
import { IMG_STORAGE_URL } from '@src/utils/constants'

import {
	CellRefundAction,
	CellRefundDate,
	CellSku,
	CellTotalPayment,
	CellUserNo,
	ExpandedRow,
	ModalApprove,
	ModalReject,
} from './components'
import { RefundTableItem } from './table.common'
import { getDateByTimezone } from '@src/utils/utils'

const { Title } = Typography

export const RefundList: React.FC = () => {
	const [messageApi, messageContextHolder] = message.useMessage()

	const [isRejectModalOpened, setIsRejectModalOpened] = useState(false)
	const [isApproveModalOpened, setIsApproveModalOpened] = useState(false)

	const [rejectReason, setRejectReason] = useState<string>()
	const [approveModalContent, setApproveModalContent] = useState<{
		refundAmount: number
		orderNo: string
		nickname: string
		product: string
		paymentDatetime: string
		paymentAmount: number
	}>()

	const [modalFocusedIndex, setModalFocusedIndex] = useState<number>()

	const [preview, _setPreview] = useState<{
		isVisible: boolean
		current: number
		images: Array<{ taskNo: string; image: string }>
	}>({
		isVisible: false,
		current: 0,
		images: [],
	})
	const setPreview = (
		previewPartial: Partial<{
			isVisible: boolean
			current: number
			images: Array<{ taskNo: string; image: string }>
		}>,
	) => {
		_setPreview((prev) => ({ ...prev, ...previewPartial }))
	}

	const [currentPage, setCurrentPage] = useState(1)
	const itemPerPage = 10

	const [pagination, _setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: itemPerPage,
		showSizeChanger: false,
		onChange: (page) => {
			setCurrentPage(page)
		},
		position: ['bottomCenter'],
	})

	const {
		refetch,
		refunds,
		total,
		isFetching,
		isRefetching,
		rejectPayback,
		isRejecting,
		confirmPayback,
		isConfirming,
	} = useRefundList({
		itemPerPage,
		currentPage,
	})

	useEffect(() => {
		_setPagination((prev) => ({
			...prev,
			current: currentPage,
			total,
		}))
	}, [currentPage, total])

	const refundTableItems = useMemo<RefundTableItem[]>(
		() =>
			refunds.map<RefundTableItem>((refund, index) => ({
				no: refund.no,
				userNo: refund.user_no,
				nickname: refund.user?.nickname,
				productImage: `${IMG_STORAGE_URL}/commerce/product/banner_img/${refund.order_item.archived_product.banner_img[0]}`,
				product: refund.order_item.archived_product.title,
				option: `${refund.order_item.archived_product.title} / ${refund.order_item.quantity}개`, // WARN: 현재 여러 개 구매 안됨, 옵션이 있는 상품이 없음
				status: refund.status,
				refundDate: refund.processed_date ? new Date(refund.processed_date) : null,
				totalPayment: refund.order_item.archived_sku.price,
				paymentMethod: refund.payment_type ? refund.payment_type : '-',
				certifications: refund.tasks.reduce<
					Array<{ taskNo: string; certificatedDatetime: Date; certificatedImage: string }>
				>((prev, cur) => {
					if (cur.task_result === null) {
						return [...prev]
					}

					return [
						...prev,
						{
							taskNo: cur.no,
							certificatedDatetime: new Date(cur.task_result.create_date),
							certificatedImage: `${IMG_STORAGE_URL}/task_original_img/${
								cur.task_result?.proof_original.at(0) || 'undefined'
							}`,
						},
					]
				}, []),
				onClickApprove: () => {
					setModalFocusedIndex(index)
					setApproveModalContent({
						refundAmount: refund.refund_price,
						orderNo: refund.order_item_no,
						nickname: refund.user.nickname,
						product: refund.order_item.archived_product.title,
						paymentDatetime: getDateByTimezone(
							'Asia/Seoul',
							'yyyy-MM-dd HH:mm:dd',
							refund.order_item.create_date,
						), // TODO,
						paymentAmount: refund.order_item.archived_sku.price,
					})
					setIsApproveModalOpened(true)
				},
				onClickReject: () => {
					setModalFocusedIndex(index)
					setIsRejectModalOpened(true)
				},
				onClickPreview: (initCurrent: number) => {
					setPreview({
						isVisible: true,
						current: initCurrent,
						images: refund.tasks.reduce<Array<{ taskNo: string; image: string }>>((prev, cur) => {
							if (cur.task_result === null) {
								return [...prev]
							}

							return [
								...prev,
								{
									taskNo: cur.no,
									image: `${IMG_STORAGE_URL}/task_original_img/${
										cur.task_result.proof_original.at(0) || 'undefined'
									}`,
								},
							]
						}, []),
					})
				},
			})),
		[refunds],
	)

	const cancelReject = () => {
		setModalFocusedIndex(undefined)
		setRejectReason(undefined)
		setIsRejectModalOpened(false)
	}

	const cancelApprove = () => {
		setApproveModalContent(undefined)
		setModalFocusedIndex(undefined)
		setIsApproveModalOpened(false)
	}

	const reject = async () => {
		if (!rejectReason || modalFocusedIndex === undefined) {
			await messageApi.info('사유를 선택해주세요')
			return
		}

		rejectPayback({
			paybackNo: refundTableItems[modalFocusedIndex].no.toString(),
			reason: rejectReason,
			onSuccess: async () => {
				setModalFocusedIndex(undefined)
				setRejectReason(undefined)
				setIsRejectModalOpened(false)
				refetch().catch((e) => console.log(e))
				await messageApi.success('환급 거절 완료')
			},
			onError: async () => {
				await messageApi.error('환급 거절 실패')
			},
		})
	}

	const confirm = () => {
		if (modalFocusedIndex === undefined || !approveModalContent) {
			return
		}

		confirmPayback({
			paybackNo: refundTableItems[modalFocusedIndex].no.toString(),
			amount: approveModalContent.refundAmount,
			onSuccess: async () => {
				setApproveModalContent(undefined)
				setModalFocusedIndex(undefined)
				setIsApproveModalOpened(false)
				refetch().catch((e) => console.log(e))
				await messageApi.success('환급 승인 완료')
			},
			onError: async () => {
				await messageApi.error('환급 승인 실패')
			},
		})
	}

	return (
		<>
			{messageContextHolder}
			<Container>
				<Title level={3}>환급 내역 조회</Title>

				{/* <Control></Control> */}

				<Table
					rowKey={(record) => record.no}
					columns={refundTableColumns}
					dataSource={refundTableItems}
					expandable={{
						expandedRowRender: ExpandedRow,
					}}
					bordered
					loading={isFetching || isRefetching}
					pagination={pagination}
					size="small"
				/>
			</Container>

			<ModalReject
				isOpened={isRejectModalOpened}
				isLoading={isRejecting}
				reason={rejectReason}
				onReject={reject}
				onCancel={cancelReject}
				onSelectReason={(reason) => setRejectReason(reason)}
			/>

			<ModalApprove
				isOpened={isApproveModalOpened}
				isLoading={isConfirming}
				refundInfo={approveModalContent}
				onApprove={confirm}
				onCancel={cancelApprove}
			/>

			<div style={{ display: 'none' }}>
				<Image.PreviewGroup
					preview={{
						visible: preview.isVisible,
						current: preview.current,
						onVisibleChange: (vis) => {
							if (vis) {
								return
							}

							setPreview({
								isVisible: false,
								images: [],
							})
						},
					}}
				>
					{preview.images.map(({ taskNo, image }) => (
						<Image key={taskNo} src={image} />
					))}
				</Image.PreviewGroup>
			</div>
		</>
	)
}

const refundTableColumns: ColumnsType<RefundTableItem> = [
	{
		title: '유저넘버',
		dataIndex: 'userNo',
		align: 'center',
		render: CellUserNo,
	},
	{
		title: '닉네임',
		dataIndex: 'nickname',
		align: 'center',
	},
	{
		title: '상품명 / 옵션',
		align: 'center',
		render: CellSku,
	},
	{
		title: '환급처리',
		align: 'center',
		render: CellRefundAction,
	},
	{
		title: '환급날짜',
		dataIndex: 'refundDate',
		align: 'center',
		render: CellRefundDate,
	},
	{
		title: '총 결제금액',
		dataIndex: 'totalPayment',
		align: 'center',
		render: CellTotalPayment,
	},
	{
		title: '결제수단',
		dataIndex: 'paymentMethod',
		align: 'center',
	},
]

const Container = styled.div`
	padding: 0 36px;
	max-width: 1440px;
`
